<template>
  <section class="weekly-card" aria-label="Weekly actions">
    <header class="weekly-card__header">
      <h3 class="weekly-card__title">Weekly actions</h3>
      <p class="weekly-card__subtitle">
        Checkpoints evaluated, inspections, pay plan proposals and tasks completed — last 12 weeks.
      </p>
      <ul class="weekly-card__legend">
        <li v-for="series in legend" :key="series.key" class="weekly-card__legend-item">
          <span class="weekly-card__legend-dot" :style="{ background: series.color }"></span>
          {{ series.label }}
        </li>
      </ul>
    </header>

    <div v-if="isEmpty" class="weekly-card__empty">No weekly actions yet.</div>

    <div v-else class="weekly-card__chart" role="img" :aria-label="chartAriaLabel">
      <svg
        :viewBox="`0 0 ${width} ${height}`"
        class="weekly-card__svg"
        preserveAspectRatio="xMidYMid meet"
      >
        <!-- horizontal gridlines + y-axis tick labels -->
        <g v-for="tick in yTicks" :key="`y-${tick}`">
          <line
            :x1="plotLeft"
            :x2="plotRight"
            :y1="yFor(tick)"
            :y2="yFor(tick)"
            class="weekly-card__gridline"
          />
          <text :x="plotLeft - 8" :y="yFor(tick) + 4" text-anchor="end" class="weekly-card__tick">
            {{ Math.round(tick) }}
          </text>
        </g>

        <!-- series polylines -->
        <polyline
          v-for="series in plotSeries"
          :key="series.key"
          :points="series.points"
          :stroke="series.color"
          fill="none"
          stroke-width="2"
          stroke-linejoin="round"
          stroke-linecap="round"
        />

        <!-- x-axis labels (week of) -->
        <g v-for="(week, index) in weeks" :key="`x-${index}`">
          <line
            :x1="xFor(index)"
            :x2="xFor(index)"
            :y1="plotTop"
            :y2="plotBottom"
            class="weekly-card__gridline weekly-card__gridline--faint"
          />
          <text
            :x="xFor(index)"
            :y="plotBottom + 18"
            text-anchor="middle"
            class="weekly-card__week-label"
          >
            {{ formatWeek(week) }}
          </text>
        </g>

        <!-- axis captions -->
        <text :x="plotLeft" :y="height - 4" class="weekly-card__axis-caption">Week of</text>
        <text
          :x="10"
          :y="(plotTop + plotBottom) / 2"
          text-anchor="middle"
          transform="rotate(-90 10 190)"
          class="weekly-card__axis-caption"
        >
          Actions
        </text>
      </svg>
    </div>
  </section>
</template>

<script setup>
import { computed, defineProps } from "vue";
import { format, isValid, parseISO } from "date-fns";

const props = defineProps({
  weeks: {
    type: Array,
    default: () => [],
  },
  series: {
    type: Object,
    default: () => ({}),
  },
});

const SERIES_DEFS = [
  { key: "tasks_completed", label: "Tasks completed", color: "#7f56d9" },
  { key: "checkpoints_inspections", label: "Checkpoints & inspections", color: "#2e90fa" },
  { key: "pay_plan_proposals", label: "Pay plan proposals", color: "#12b76a" },
];

const legend = computed(() => SERIES_DEFS);

const width = 1100;
const height = 240;
const plotLeft = 44;
const plotRight = width - 16;
const plotTop = 12;
const plotBottom = 190;

const weeks = computed(() => props.weeks || []);

function seriesValues(key) {
  const s = props.series || {};
  const value = s[key];
  return Array.isArray(value) ? value : [];
}

const plotSeries = computed(() => {
  return SERIES_DEFS.map((def) => {
    const values = seriesValues(def.key);
    const n = weeks.value.length;
    const points = values
      .slice(0, Math.max(n, 0))
      .map((value, index) => `${xFor(index)},${yFor(value)}`)
      .join(" ");
    return { key: def.key, color: def.color, points };
  });
});

const maxValue = computed(() => {
  let max = 0;
  SERIES_DEFS.forEach((def) => {
    seriesValues(def.key).forEach((value) => {
      const n = Number(value);
      if (Number.isFinite(n)) max = Math.max(max, n);
    });
  });
  return max > 0 ? max : 10;
});

const yTicks = computed(() => {
  const max = maxValue.value;
  const step = niceStep(max);
  const ticks = [];
  for (let v = 0; v <= max + step; v += step) {
    ticks.push(v);
  }
  return ticks;
});

const isEmpty = computed(() => {
  return SERIES_DEFS.every((def) => seriesValues(def.key).length === 0);
});

const chartAriaLabel = computed(() => {
  return "Weekly actions over the last 12 weeks: " + legend.value.map((s) => s.label).join(", ");
});

function niceStep(max) {
  const rough = max / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough || 1)));
  const normalized = rough / magnitude;
  const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return nice * magnitude;
}

function xFor(index) {
  const n = weeks.value.length;
  if (n <= 1) return plotLeft;
  return plotLeft + (index / (n - 1)) * (plotRight - plotLeft);
}

function yFor(value) {
  const n = Number(value);
  const max = maxValue.value;
  const safe = Number.isFinite(n) ? n : 0;
  return plotBottom - (safe / max) * (plotBottom - plotTop);
}

function formatWeek(week) {
  if (week == null) return "";
  const parsed = typeof week === "string" ? parseISO(week) : new Date(week);
  if (isValid(parsed)) return format(parsed, "d MMM");
  return String(week);
}
</script>

<style scoped>
.weekly-card {
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  padding: 20px 24px 24px;
  overflow: hidden;
}

.weekly-card__header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.weekly-card__title {
  margin: 0;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.weekly-card__subtitle {
  margin: 0;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

.weekly-card__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
}

.weekly-card__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #475467;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 18px;
}

.weekly-card__legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  flex-shrink: 0;
}

.weekly-card__empty {
  padding: 32px 0 8px;
  color: #667085;
  font-family: Inter, sans-serif;
  font-size: 14px;
}

.weekly-card__chart {
  margin-top: 12px;
}

.weekly-card__svg {
  width: 100%;
  height: auto;
  display: block;
}

.weekly-card__gridline {
  stroke: #eaecf0;
  stroke-width: 1;
}

.weekly-card__gridline--faint {
  stroke: #f2f4f7;
}

.weekly-card__tick {
  fill: #667085;
  font-family: Inter, sans-serif;
  font-size: 11px;
}

.weekly-card__week-label {
  fill: #667085;
  font-family: Inter, sans-serif;
  font-size: 11px;
}

.weekly-card__axis-caption {
  fill: #98a2b3;
  font-family: Inter, sans-serif;
  font-size: 11px;
}
</style>
